import React, { useState } from 'react';
import { useConversations } from '../hooks/useConversations';
import { ConversationList } from '../components/conversations/ConversationList';
import { ConversationDetails } from '../components/conversations/ConversationDetails';
import { AlertDialog } from '../components/ui/AlertDialog';
import { ArrowLeft, MessageSquare, MessagesSquare } from 'lucide-react';

// Style pour les barres de défilement
const customScrollbarStyle = {
  scrollbarWidth: 'thin' as 'thin',
  scrollbarColor: 'rgba(99, 102, 241, 0.5) rgba(20, 20, 30, 0.5)'
};

export default function Conversations() {
  const { 
    conversations, 
    loading, 
    error, 
    selectedConversation, 
    selectConversation,
    deleteConversation,
    deleteAllConversations
  } = useConversations();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
  const [viewingDetails, setViewingDetails] = useState(false);

  const handleDelete = async () => {
    if (conversationToDelete) {
      await deleteConversation(conversationToDelete);
      setDeleteDialogOpen(false);
      setConversationToDelete(null);
    }
  };

  const handleDeleteAll = async () => {
    await deleteAllConversations();
    setDeleteAllDialogOpen(false);
  };

  const handleSelectConversation = (id: string) => {
    selectConversation(id);
    setViewingDetails(true);
  };

  const handleBackToList = () => {
    setViewingDetails(false);
  };

  return (
    <div className="p-4 md:p-8 bg-gradient-to-b from-gray-900 to-black min-h-screen">
      <div className="mb-6 md:mb-8 pb-4 border-b border-white/10">
        {viewingDetails && selectedConversation ? (
          <div className="flex items-center gap-3 animate-fade-in">
            <button 
              onClick={handleBackToList}
              className="flex items-center justify-center p-2 rounded-full bg-gradient-to-r from-violet-600/20 to-cyan-500/20 text-white transition-colors md:hidden"
              aria-label="Back to conversations list"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white mb-2 flex items-center gap-2">
                <span className="text-white hidden md:inline">
                  <MessageSquare className="w-6 h-6 inline-block mr-2" />
                </span>
                Conversation Details
              </h1>
              <p className="text-gray-400 text-sm md:text-base">View and manage this conversation</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 animate-fade-in">
            <div className="flex items-center gap-2 bg-gradient-to-r from-violet-600/20 to-cyan-500/20 p-2 rounded-full mr-3 text-white hidden md:flex">
              <MessagesSquare className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white mb-2 group">
                Conversations
                <span className="inline-block ml-2 bg-gradient-to-r from-violet-600/20 to-cyan-500/20 text-white text-sm px-2.5 py-0.5 rounded-full">
                  {conversations.length}
                </span>
              </h1>
              <p className="text-gray-400 text-sm md:text-base">Review your conversation history with users</p>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        <div 
          className={`lg:col-span-1 overflow-y-auto max-h-[calc(100vh-13rem)] ${viewingDetails ? 'hidden md:block' : 'block'} animate-fade-in`} 
          style={customScrollbarStyle}
        >
          <ConversationList 
            conversations={conversations}
            loading={loading}
            error={error}
            selectedId={selectedConversation?.id}
            onSelect={handleSelectConversation}
            onDelete={(id) => {
              setConversationToDelete(id);
              setDeleteDialogOpen(true);
            }}
            onDeleteAll={() => setDeleteAllDialogOpen(true)}
          />
        </div>
        <div className={`lg:col-span-2 ${viewingDetails ? 'block animate-slide-in-right' : 'hidden md:block'}`}>
          <div className="sticky top-8">
            <ConversationDetails 
              conversation={selectedConversation} 
              onBack={handleBackToList}
              showBackButton={viewingDetails}
            />
          </div>
        </div>
      </div>

      {/* Dialog de confirmation pour la suppression d'une conversation */}
      <AlertDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete conversation"
        description="Are you sure you want to delete this conversation? This action is irreversible."
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
      />

      {/* Dialog de confirmation pour la suppression de toutes les conversations */}
      <AlertDialog
        isOpen={deleteAllDialogOpen}
        onClose={() => setDeleteAllDialogOpen(false)}
        title="Delete all conversations"
        description="Are you sure you want to delete all conversations? This action is irreversible."
        confirmLabel="Delete all"
        confirmVariant="danger"
        onConfirm={handleDeleteAll}
      />
    </div>
  );
}